import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import Sidebar from './Sidebar';

interface LayoutProps {
    children: React.ReactNode;
}

interface ProfileData {
    displayName: string | null;
    email: string;
    photoURL: string | null;
    role: 'teacher' | 'student';
}

export default function Layout({ children }: LayoutProps) {
    const [user, setUser] = useState<any>(null);
    const [profileData, setProfileData] = useState<ProfileData | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) setSidebarOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        document.body.style.overflow = isMobile && sidebarOpen ? 'hidden' : '';

        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobile, sidebarOpen]);

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setUser(session?.user ?? null);
        });
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setUser(session?.user ?? null);
        });
        return () => subscription.unsubscribe();
    }, []);

    // Fetch profile photo from correct table based on role
    useEffect(() => {
        if (!user) {
            setProfileData(null);
            return;
        }

        const fetchProfile = async () => {
            const userRole = localStorage.getItem('userRole');
            const email = user.email;

            let displayName = user.user_metadata?.full_name || user.user_metadata?.name || null;
            let photoURL = user.user_metadata?.avatar_url || null;

            if (userRole === 'teacher') {
                // Fetch from teachers table
                const { data } = await supabase
                    .from('teachers')
                    .select('name, image_url')
                    .eq('email', email)
                    .single();

                if (data) {
                    displayName = data.name || displayName;
                    photoURL = data.image_url || photoURL;
                }
            } else if (userRole === 'student') {
                // Fetch from students table
                const { data } = await supabase
                    .from('students')
                    .select('name, avatar_url')
                    .eq('email', email)
                    .single();

                if (data) {
                    displayName = data.name || displayName;
                    photoURL = data.avatar_url || photoURL;
                }
            }

            setProfileData({
                displayName,
                email,
                photoURL,
                role: (userRole as 'teacher' | 'student') || 'student',
            });
        };

        fetchProfile();
    }, [user]);

    const sidebarUser = profileData;

    if (sidebarUser) {
        return (
            <div style={{ display: 'flex', minHeight: '100vh' }}>
                {/* Mobile hamburger button */}
                {isMobile && (
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        aria-label="Toggle menu"
                        style={{
                            position: 'fixed', right: '1rem', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)', zIndex: 200,
                            width: '48px', height: '48px', borderRadius: '16px',
                            border: '1px solid rgba(148,163,184,0.22)', background: 'rgba(255,255,255,0.96)',
                            backdropFilter: 'blur(16px)', boxShadow: '0 18px 40px rgba(15,23,42,0.12)',
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            justifyContent: 'center', gap: sidebarOpen ? '0px' : '4px',
                            cursor: 'pointer', padding: 0, transition: 'all 0.2s ease',
                        }}
                    >
                        <span style={{ width: '18px', height: '2px', background: '#1e293b', borderRadius: '1px', transition: 'all 0.3s ease', transform: sidebarOpen ? 'rotate(45deg) translate(1px, 1px)' : 'none' }} />
                        <span style={{ width: '18px', height: '2px', background: '#1e293b', borderRadius: '1px', transition: 'all 0.3s ease', opacity: sidebarOpen ? 0 : 1 }} />
                        <span style={{ width: '18px', height: '2px', background: '#1e293b', borderRadius: '1px', transition: 'all 0.3s ease', transform: sidebarOpen ? 'rotate(-45deg) translate(1px, -1px)' : 'none' }} />
                    </button>
                )}

                {/* Overlay backdrop for mobile */}
                {isMobile && sidebarOpen && (
                    <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 149, backdropFilter: 'blur(4px)', transition: 'opacity 0.3s' }} />
                )}

                <Sidebar user={sidebarUser} isMobile={isMobile} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
                <main style={{ flex: 1, marginLeft: isMobile ? '0' : '288px', minHeight: '100vh', background: '#f8fafc', transition: 'margin-left 0.3s ease', width: isMobile ? '100%' : 'auto', overflow: 'hidden', paddingBottom: isMobile ? '5.5rem' : '0' }}>
                    {children}
                </main>
            </div>
        );
    }

    return <>{children}</>;
}
